// components/AuthPage/FloatingInput.jsx
import { useState } from "react";

const EyeOpen = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

/**
 * FloatingInput — floating label input with optional password toggle & suffix icon
 * Props: name, label, type, value, onChange, required, autoComplete,
 *        suffixIcon (ReactNode), className
 */
export default function FloatingInput({
  name, label, type = "text", value, onChange,
  required, autoComplete, suffixIcon, className = "",
}) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const inputType  = isPassword ? (showPw ? "text" : "password") : type;
  const hasSuffix  = isPassword || !!suffixIcon;

  return (
    <div className={`relative ${className}`}>
      <input
        name={name}
        type={inputType}
        placeholder=" "
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        autoCapitalize={type === "email" || name === "username" ? "none" : undefined}
        spellCheck={name === "username" ? "false" : undefined}
        /* Tailwind classes */
        className={[
          "peer w-full rounded-[10px] border border-white/[0.07]",
          "bg-[rgba(13,21,38,0.7)] text-slate-200 text-[13.5px]",
          "pt-5 pb-2 pl-[14px]",
          hasSuffix ? "pr-10" : "pr-[14px]",
          "outline-none transition-all duration-200 appearance-none",
          "placeholder-transparent",
          "focus:bg-[rgba(37,99,235,0.07)] focus:border-blue-500/55",
          "focus:ring-[3px] focus:ring-blue-500/10",
          "autofill:shadow-[inset_0_0_0_30px_#0d1526] autofill:[-webkit-text-fill-color:#e2e8f0]",
        ].join(" ")}
      />

      {/* Floating label */}
      <label
        className={[
          "absolute left-[14px] text-slate-500/60 pointer-events-none transition-all duration-200",
          // default: vertically centered, full size
          "top-1/2 -translate-y-1/2 text-[13.5px]",
          // when focused or filled: float up
          "peer-focus:top-[9px] peer-focus:translate-y-0 peer-focus:text-[10px]",
          "peer-focus:text-blue-400 peer-focus:tracking-[.06em] peer-focus:uppercase",
          "peer-[:not(:placeholder-shown)]:top-[9px]",
          "peer-[:not(:placeholder-shown)]:translate-y-0",
          "peer-[:not(:placeholder-shown)]:text-[10px]",
          "peer-[:not(:placeholder-shown)]:text-blue-400",
          "peer-[:not(:placeholder-shown)]:tracking-[.06em]",
          "peer-[:not(:placeholder-shown)]:uppercase",
        ].join(" ")}
      >
        {label}
      </label>

      {/* Password eye toggle */}
      {isPassword && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShowPw((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500/50 hover:text-blue-400 transition-colors p-0.5 flex"
        >
          {showPw ? <EyeOff /> : <EyeOpen />}
        </button>
      )}

      {/* Custom suffix icon (non-password) */}
      {!isPassword && suffixIcon && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex">
          {suffixIcon}
        </span>
      )}
    </div>
  );
}